<template lang="pug">
.appointment-calendar-left-panel.pb-4
  //- Mini calendar
  yc-date-picker.range-date-picker(:parentDate.sync="selectedDates" :parentPickerDate.sync="pickerDate" range :show-adjacent-months="showAdjacentMonths" first-day-of-week="1" @input="onSelectedDate")
  v-divider.my-4
  //- Providers
  .providers-wrapper
    .d-flex.justify-space-between.align-center.mb-4
      .text-sm.font-semibold.pl-2 Providers
      .text-xs.font-semibold.underline.mr-2.clickable.hover-bg.px-2.py-1.rounded-lg(@click="onSelectAll") Select all
    template(v-if="isFetching")
      .px-1
        v-skeleton-loader.pb-2(type="text")
        v-skeleton-loader.pb-2(type="text")
        v-skeleton-loader(type="text")
    template(v-else)
      template(v-if="providers.length > 0")
        .overflow-y-auto(:class="[providersHeight]")
          div(v-for="provider in providers" :key="provider.value")
            .d-flex.text-sm.px-2.py-3.cursor-pointer.hover-bg(@click="clickItem(provider)")
              yc-checkbox(:input-value="isChecked(provider.value)" @change="clickItem(provider)" :color="provider.color")
              .ml-4
                div {{ provider.label }}
                .text-xs.neutral500--text(v-if="isHq") {{ provider.company }}
      template(v-else)
          .text-center.py-8.text-xs.neutral500--text {{ $t('no_doctors_found') }}
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    parentProviders: {
      type: Array,
      default: () => {
        return []
      }
    },
    parentDates: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      selectedDates: null,
      providers: [],
      selectedProviders: [],
      isFetching: true,
      pickerDate: null,
      showAdjacentMonths: false
    }
  },
  created () {
    this.setupDate()
  },
  async mounted () {
    await this.fetchProviders()
    this.onSelectAll()
  },
  watch: {
    parentDates (value) {
      this.selectedDates = [...value]
      const startMonthView = value[0].slice(0,7)
      const endMonthView = value[1].slice(0,7)
      this.showAdjacentMonths = startMonthView !== endMonthView
      this.pickerDate = startMonthView
    },
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      displaySize: 'ui/getDisplaySize',
      isHq: 'hq/getIsHq',
    }),
    providersHeight () {
      return `providers-height-${this.displaySize}`
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    setupDate () {
      const now = DateTime.local()
      const dateFormat = 'yyyy-MM-dd'
      
      this.selectedDates = [now.toFormat(dateFormat), now.toFormat(dateFormat)]
      this.$emit('update:parentDates', [...this.selectedDates])
    },
    async fetchProviders () {
      try {
        this.isFetching = true
        const params = {
          fields: 'id,name,member,extra_data,profile_image,company',
          page_size: -1,
          expand: 'company'
        }
        if (this.isHq) {
          params.hq = this.company.id
        }
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/providers`, { params })
        if (data.results) {
          this.providers = data.results.map(result => {
            return {
              id: result.id,
              label: result.name,
              value: result.member,
              color: result?.extra_data?.tag_color || null,
              image: result.profile_image,
              company: result.company.name
            } 
          })
        }
        this.isFetching = false
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    clickItem (item) {
      if (this.selectedProviders.map(i => i.value).includes(item.value)) {
        this.selectedProviders = this.selectedProviders.filter(x => x.value !== item.value)
      } else {
        this.selectedProviders.push(item)
      }
      this.$emit('update:parentProviders', [...this.selectedProviders])
    },
    isChecked (value) {
      return this.selectedProviders.filter(item => item.value === value).length > 0
    },
    onSelectAll () {
      this.selectedProviders = [...this.providers]
      this.$emit('update:parentProviders', [...this.selectedProviders])
    },
    onSelectedDate () {
      const dateFormat = 'yyyy-MM-dd'
      const datetime = DateTime.fromISO(this.selectedDates[0])
      this.selectedDates = [datetime.toFormat(dateFormat), datetime.toFormat(dateFormat)]
      this.$emit('selected', [...this.selectedDates])
    }
  },
}
</script>

<style lang="scss" scoped>
// Use flexbox layout to fill available space in both desktop and mobile
.appointment-calendar-left-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .providers-wrapper {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

::v-deep .range-date-picker {

  .v-date-picker-table {
    height: 100%;
  }

  .v-date-picker-table .v-btn.v-date-picker-table__current {
    color: #333333 !important;
  }

  .v-date-picker-table .v-btn.v-btn--active {
    background-color: #EEEEEE !important;
    color: #333333 !important;
    border: none !important;
  }

  .v-date-picker-table .v-btn.v-btn--active.v-date-picker--first-in-range, .v-btn.v-btn--active.v-date-picker--last-in-range {
    background-color: #333333 !important;
    .v-btn__content {
      color: #ffffff;
    }
  }

  .v-date-picker-table .v-btn {
    font-weight: 400;
    font-size: 12px !important;
    border-radius: 8px !important;
    padding: 0 !important;
    margin: 4px !important;
    height: 25px;
    width: 25px;
    background-color: #F9FAFB !important;
  }

  .v-date-picker-table__current {
    border: 2px solid black !important;
  }
}
</style>